import { contextualSegmentPrefix } from '@openx/types/targeting/contextualSegments';
import type { CustomVariablesDataPayload } from '@openx/types/targeting/customVariables';
import { TargetingFields, type TargetingState } from '@openx/types/targeting/targeting';
import { ComparisonType } from '@openx/types/targeting/targetingValuesTypes';

export const CONTEXTUAL_SEGMENTS_SAVED_EVENTS = {
  deal: 'ContextualMarketplace_deal_saved',
  package: 'ContextualMarketplace_package_saved',
} as const;

export type ContextualSegmentsStats = {
  providersCount: number;
  includedCount: number;
  excludedCount: number;
};

const splitSegmentIds = (val?: string | null): string[] =>
  (val ?? '')
    .split(',')
    .map(id => id.trim())
    .filter(Boolean);

const getSegments = (targeting?: Partial<TargetingState> | null): CustomVariablesDataPayload[] =>
  targeting?.[TargetingFields.CONTEXTUAL_SEGMENTS] ?? [];

const countSegmentIds = (segments: CustomVariablesDataPayload[], op: ComparisonType): number =>
  segments.filter(segment => segment.op === op).reduce((sum, { val }) => sum + splitSegmentIds(val).length, 0);

/**
 * Reads only the contextual segments form-state field, which is format-agnostic
 * (the API mapping happens later, when the package is serialized).
 */
export const getContextualSegmentsStats = (targeting?: Partial<TargetingState> | null): ContextualSegmentsStats => {
  const segments = getSegments(targeting);
  const includedCount = countSegmentIds(segments, ComparisonType.INTERSECTS);
  const excludedCount = countSegmentIds(segments, ComparisonType.NOT_INTERSECTS);
  const providersCount = new Set(segments.filter(({ val }) => splitSegmentIds(val).length).map(({ attr }) => attr))
    .size;

  return { providersCount, includedCount, excludedCount };
};

/** Order- and prefix-insensitive set of `provider|op|segmentId` keys (V2 keeps the `ctx_` prefix, V3 does not). */
const getSegmentKeys = (targeting?: Partial<TargetingState> | null): Set<string> => {
  const keys = getSegments(targeting).flatMap(({ attr, op, val }) => {
    const provider = attr.startsWith(contextualSegmentPrefix) ? attr.slice(contextualSegmentPrefix.length) : attr;
    return splitSegmentIds(val).map(id => `${provider}|${op}|${id}`);
  });

  return new Set(keys);
};

export const haveContextualSegmentsChanged = (
  targeting?: Partial<TargetingState> | null,
  initialTargeting?: Partial<TargetingState> | null
): boolean => {
  const current = getSegmentKeys(targeting);
  const initial = getSegmentKeys(initialTargeting);

  return current.size !== initial.size || [...current].some(key => !initial.has(key));
};

type TrackContextualSegmentsSavedParams = {
  entity: keyof typeof CONTEXTUAL_SEGMENTS_SAVED_EVENTS;
  isEdit: boolean;
  /** Only the standalone (V3) contextual segments flow is tracked. */
  isStandaloneContextualSegments: boolean;
  /** Targeting the entity ends up with after save (may differ from the form, e.g. locked package used as-is). */
  targeting?: Partial<TargetingState> | null;
  initialTargeting?: Partial<TargetingState> | null;
  /** Package saved as a step of the deal form flow (the deal save is tracked separately), so it can be told apart from standalone package saves. */
  fromDealForm?: boolean;
};

export const trackContextualSegmentsSaved = ({
  entity,
  isEdit,
  isStandaloneContextualSegments,
  targeting,
  initialTargeting,
  fromDealForm = false,
}: TrackContextualSegmentsSavedParams): void => {
  if (!window.pendo || !isStandaloneContextualSegments) return;

  // Analytics must never break the save flow (this runs before post-save navigation).
  try {
    const stats = getContextualSegmentsStats(targeting);
    /** Only saves with contextual segments count towards adoption, so removing all of them is not tracked */
    if (!stats.includedCount && !stats.excludedCount) return;

    const initialStats = getContextualSegmentsStats(initialTargeting);

    window.pendo.track(CONTEXTUAL_SEGMENTS_SAVED_EVENTS[entity], {
      is_edit: isEdit,
      version: 'v3',
      providers_count: stats.providersCount,
      included_count: stats.includedCount,
      excluded_count: stats.excludedCount,
      /** Form started with segments — e.g. editing a contextual deal, or creating one from a template/package. */
      had_segments_before: !!(initialStats.includedCount || initialStats.excludedCount),
      segments_changed: haveContextualSegmentsChanged(targeting, initialTargeting),
      ...(entity === 'package' && { from_deal_form: fromDealForm }),
    });
  } catch (error) {
    // biome-ignore lint/suspicious/noConsole: tracking failure should be visible but non-blocking
    console.error(error);
  }
};
